import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Bell, MessageCircle, CheckCheck, Loader2, UserPlus, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import api from "@/lib/api";
import { formatDateTime } from "@/lib/format";

const POLL_MS = 25000;

export const NotificationBell = () => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(false);
  const [acting, setActing] = useState(null);

  const quickAdd = async (n) => {
    setActing(n.id);
    try {
      const { data } = await api.post(`/companies/${n.company_id}/investors/quick-add`, { email: n.meta.email, send_latest_update: true });
      toast.success(data.update_sent ? `${n.meta.email} added - "${data.update_sent.title}" is on its way` : `${n.meta.email} added as an investor`);
      if (!n.is_read) setUnread((u) => Math.max(0, u - 1));
      setItems((list) => list.map((x) => (x.id === n.id ? { ...x, is_read: true, meta: { ...x.meta, handled: true } } : x)));
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not add investor"); }
    finally { setActing(null); }
  };

  const loadCount = useCallback(async () => {
    try { const { data } = await api.get(`/notifications/unread-count`); setUnread(data.count || 0); }
    catch (e) { console.error("Failed to load notification count", e); }
  }, []);

  const loadList = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get(`/notifications`, { params: { limit: 20 } }); setItems(data); }
    catch (e) { console.error("Failed to load notifications", e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    loadCount();
    const t = setInterval(loadCount, POLL_MS);
    const onFocus = () => loadCount();
    window.addEventListener("focus", onFocus);
    return () => { clearInterval(t); window.removeEventListener("focus", onFocus); };
  }, [loadCount]);

  useEffect(() => { if (open) loadList(); }, [open, loadList]);

  const openNotification = async (n) => {
    setOpen(false);
    if (!n.is_read) {
      try { await api.post(`/notifications/${n.id}/read`); } catch (e) { console.error("Failed to mark notification read", e); }
      setUnread((u) => Math.max(0, u - 1));
      setItems((list) => list.map((x) => (x.id === n.id ? { ...x, is_read: true } : x)));
    }
    if (n.link) navigate(n.link);
  };

  const markAll = async () => {
    try {
      await api.post(`/notifications/read-all`);
      setUnread(0);
      setItems((list) => list.map((x) => ({ ...x, is_read: true })));
    } catch (e) { console.error("Failed to mark all notifications read", e); toast.error("Could not mark all as read"); }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="icon" className="relative" data-testid="notification-bell" aria-label="Notifications">
          <Bell className="h-5 w-5" />
          {unread > 0 && (
            <span
              data-testid="notification-unread-badge"
              className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-primary px-1 text-[10px] font-semibold leading-none text-primary-foreground"
            >
              {unread > 9 ? "9+" : unread}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-[360px] p-0" data-testid="notification-panel">
        <div className="flex items-center justify-between border-b px-4 py-3">
          <div className="flex items-center gap-2">
            <span className="text-sm font-semibold text-foreground">Notifications</span>
            {unread > 0 && <Badge variant="secondary" className="font-normal">{unread} new</Badge>}
          </div>
          {items.some((n) => !n.is_read) && (
            <Button variant="ghost" size="sm" className="h-7 gap-1.5 px-2 text-xs" onClick={markAll} data-testid="notification-mark-all">
              <CheckCheck className="h-3.5 w-3.5" /> Mark all read
            </Button>
          )}
        </div>
        <ScrollArea className="max-h-[380px]">
          {loading ? (
            <div className="flex items-center justify-center py-10 text-muted-foreground"><Loader2 className="h-5 w-5 animate-spin" /></div>
          ) : items.length === 0 ? (
            <div className="px-4 py-10 text-center text-sm text-muted-foreground" data-testid="notification-empty">
              You're all caught up.
            </div>
          ) : (
            <ul className="divide-y">
              {items.map((n) => (
                <li key={n.id} className={n.is_read ? "opacity-70" : "bg-primary/5"}>
                  <button
                    type="button"
                    onClick={() => openNotification(n)}
                    data-testid={`notification-item-${n.id}`}
                    className="flex w-full items-start gap-3 px-4 py-3 text-left transition-colors hover:bg-secondary"
                  >
                    <span className={`mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full ${n.is_read ? "bg-secondary text-muted-foreground" : "bg-primary/15 text-primary"}`}>
                      {n.type === "investor_joined" ? <UserPlus className="h-4 w-4" /> : <MessageCircle className="h-4 w-4" />}
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm font-medium text-foreground">{n.title}</span>
                      <span className="mt-0.5 block line-clamp-2 text-xs text-muted-foreground">{n.body}</span>
                      <span className="mt-1 block text-[11px] text-muted-foreground">{formatDateTime(n.created_at)}</span>
                    </span>
                    {!n.is_read && <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-primary" aria-label="unread" />}
                  </button>
                  {n.type === "investor_joined" && n.meta?.email && (
                    <div className="px-4 pb-3 pl-[60px]">
                      {n.meta.handled ? (
                        <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-emerald-700" data-testid={`notification-added-${n.id}`}>
                          <CheckCircle2 className="h-3.5 w-3.5" /> Added as investor
                        </span>
                      ) : (
                        <Button size="sm" className="h-7 gap-1.5 text-xs" disabled={acting === n.id} onClick={() => quickAdd(n)} data-testid={`notification-quick-add-${n.id}`}>
                          {acting === n.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <UserPlus className="h-3.5 w-3.5" />} Add as investor + send latest update
                        </Button>
                      )}
                    </div>
                  )}
                </li>
              ))}
            </ul>
          )}
        </ScrollArea>
      </PopoverContent>
    </Popover>
  );
};
